<script setup lang="ts">
import type { LinkRecordsSubmit } from './LinkRecordsDialog.vue';
import type { SuggestionCluster, SuggestionListMode } from '@/components/domain/record-link/fields';
import { computed } from 'vue';
import { Separator } from '@/components/base/separator';
import SuggestionCard from './SuggestionCard.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    clusters: SuggestionCluster[]
    canManage: boolean
    mode: SuggestionListMode
    pending: boolean
    expandAll: boolean
}>();

const emit = defineEmits<{
    confirm: [suggestionIds: number[]]
    dismiss: [suggestionIds: number[]]
    reopen: [suggestionIds: number[]]
    'link-records': [payload: LinkRecordsSubmit]
}>();

const sections = computed(() => [
    { key: 'exact', title: 'Exakte Treffer', clusters: props.clusters.filter((cluster) => cluster.exact) },
    { key: 'review', title: 'Zu prüfen', clusters: props.clusters.filter((cluster) => !cluster.exact) },
].filter((section) => section.clusters.length > 0));
</script>

<template>
    <div>
        <section
            v-for="section in sections"
            :key="section.key"
            class="kgr-row"
        >
            <div class="suggestion-section__head">
                <h3 class="kgr-headline kgr-headline--h3">
                    {{ $t(section.title) }}
                </h3>
                <span class="kgr-badge kgr-badge--gray">{{ section.clusters.length }}</span>
                <Separator class="flex-1" />
            </div>
            <div class="suggestion-cards">
                <SuggestionCard
                    v-for="cluster in section.clusters"
                    :key="cluster.key"
                    :cluster="cluster"
                    :can-manage="canManage"
                    :mode="mode"
                    :pending="pending"
                    :expand-all="expandAll"
                    @confirm="(ids) => emit('confirm', ids)"
                    @dismiss="(ids) => emit('dismiss', ids)"
                    @reopen="(ids) => emit('reopen', ids)"
                    @link-records="(payload) => emit('link-records', payload)"
                />
            </div>
        </section>
    </div>
</template>

<style scoped lang="scss">
.suggestion-section__head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
}

.suggestion-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(36rem, 100%), 1fr));
    align-items: start;
    gap: 0.875rem;
}
</style>
